<template>
  <form>
    <FormHeader v-if="title" :title="title" @cancel="onCancel" @submit="onSubmit"/>
    <div class="space-y-12 mt-5">
      <FormBooleanField v-show="physicalPerson?.attributes?.isUboOnly === true" v-model="addedAsUbo" field-path="attributes.addAsUBO" :field-label="$gettext('Profile minimal UBO')" :field-attrs="addedAsUboAttrs" :errors="errors"/>
      <FormIdentificationTypeField v-if="!addedAsUbo" v-model="identificationType" field-path="attributes.identificationType" :field-label="$gettext('Moyen d\'identification')" :field-attrs="identificationTypeAttrs" :errors="errors" />
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails de la personne physique") }}</h3>
        <FormTextField v-model="lastName" field-path="attributes.lastName" :field-label="$gettext('Nom')" :field-attrs="lastNameAttrs" :errors="errors" />
        <FormTextField v-model="firstName" field-path="attributes.firstName" :field-label="$gettext('Prénom')" :field-attrs="firstNameAttrs" :errors="errors" />
        <FormDateField v-model="birthdate" field-path="attributes.birthdate" :field-label="$gettext('Date de naissance')" :field-attr="birthdateAttrs" :errors="errors" />
        <FormTextField v-model="birthCity" field-path="attributes.birthCity" :field-label="$gettext('Lieu de naissance')" :field-attrs="birthCityAttrs" :errors="errors" />
        <FormSelectField v-model="birthCountryId" field-path="relationships.birthCountry.data.id" :field-label="$gettext('Pays de naissance')" :field-attrs="birthCountryIdAttrs" :errors="errors" :options="countryOptions" />
        <FormSelectField v-if="!addedAsUbo" v-model="nationalityCountryId" field-path="relationships.nationalityCountry.data.id" :field-label="$gettext('Nationalité')" :field-attrs="nationalityCountryIdAttrs" :errors="errors" :options="nationalityOptions" />
        <FormBooleanField v-model="isPoliticalyExposed" field-path="attributes.isPoliticalyExposed" :field-label="$gettext('Politiquement exposée')" :field-attrs="isPoliticalyExposedAttrs" :errors="errors"/>
        <FormBooleanField v-model="isCloseToPoliticalyExposed" field-path="attributes.isCloseToPoliticalyExposed" :field-label="$gettext('Proche d\'un personne politiquement exposée')" :field-attrs="isCloseToPoliticalyExposedAttrs" :errors="errors"/>
      </div>
      <div class="border-b border-gray-900/10 pb-12" v-if="!addedAsUbo">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Adresse de la personne") }}</h3>
        <FormAddressAutocompleteField @update:address="handleAddressUpdate" v-model="addressStreet" field-path="attributes.addressStreet" :field-label="$gettext('Rue et numéro')" :field-attrs="addressStreetAttrs" :errors="errors" />
        <FormTextField v-model="addressZipcode" field-path="attributes.addressZipcode" :field-label="$gettext('Code postal')" :field-attrs="addressZipcodeAttrs" :errors="errors" />
        <FormTextField v-model="addressCity" field-path="attributes.addressCity" :field-label="$gettext('Ville')" :field-attrs="addressCityAttrs" :errors="errors" />
        <FormSelectField v-model="addressCountryId" field-path="relationships.addressCountry.data.id" :field-label="$gettext('Pays')" :field-attrs="addressCountryIdAttrs" :errors="errors" :options="countryOptions" />
      </div>
      <div class="border-b border-gray-900/10 pb-12" v-if="!addedAsUbo">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Activité économique de la personne physique") }}</h3>
        <FormSelectField v-model="professionId" field-path="relationships.profession.data.id" :field-label="$gettext('Profession')" :field-attrs="professionIdAttrs" :errors="errors" :options="professionOptions" />
        <FormSelectField v-model="industrySectorId" field-path="relationships.industrySector.data.id" :field-label="$gettext('Secteur d\'activité')" :field-attrs="industrySectorIdAttrs" :errors="errors" :options="industrySectorOptions" />
      </div>
      <div class="border-b border-gray-900/10 pb-12" v-if="!addedAsUbo">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
        <FormDocumentsField
          v-model="physicalPersonDocuments"
          category-name="identityDocuments"
          field-path="relationships.physicalPersonDocuments.data"
          :field-label="$gettext('Document d\'identité')"
          field-description=""
          :field-attrs="physicalPersonDocumentsAttrs" :errors="errors"/>
        <FormDocumentsField
          v-model="physicalPersonDocuments"
          category-name="otherDocuments"
          field-path="relationships.physicalPersonDocuments.data"
          :field-label="$gettext('Autre documents')"
          field-description=""
          :field-attrs="physicalPersonDocumentsAttrs" :errors="errors"/>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>

<script setup>
import { useCountryStore } from "@/stores/country.js";
import { useIndustrySectorStore } from "@/stores/industrySector.js";
import { useProfessionStore } from "@/stores/profession.js";
import { useForm } from "vee-validate";
import * as yup from "yup";
import { computed, onMounted, watch } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import FormAddressAutocompleteField from "@/components/form-fields/FormAddressAutocompleteField.vue";
import { useGettext } from "vue3-gettext";
import FormTextField from "@/components/form-fields/FormTextField.vue";
import FormDateField from "@/components/form-fields/FormDateField.vue";
import FormSelectField from "@/components/form-fields/FormSelectField.vue";
import FormIdentificationTypeField from "@/components/form-fields/FormIdentificationTypeField.vue";
import FormBooleanField from "@/components/form-fields/FormBooleanField.vue";
import FormDocumentsField from "@/components/form-fields/FormDocumentsField.vue";
import { useCountryDetection, findCountryByCode } from "@/utils/countryDetection.js";

const { $gettext } = useGettext();

const countryStore = useCountryStore();
const industrySectorStore = useIndustrySectorStore();
const professionStore = useProfessionStore();

const props = defineProps(["physicalPerson", "title", "ubo"]);
const emits = defineEmits(["submit", "cancel"]);

const countryOptions = computed(() =>
  countryStore.sortedCountries.map((country) => ({
    value: country.id,
    label: country.attributes.name,
  }))
);

const nationalityOptions = computed(() =>
  countryStore.sortedCountries.map((country) => ({
    value: country.id,
    label: country.attributes.demonym,
  }))
);

const industrySectorOptions = computed(() =>
  industrySectorStore.industrySectors.map((industrySector) => ({
    value: industrySector.id,
    label: industrySector.attributes.name,
  }))
);

const professionOptions = computed(() =>
  professionStore.professions.map((profession) => ({
    value: profession.id,
    label: profession.attributes.name,
  }))
);

const skipWhenAddedAsUbo = {
  is: true,
  then: schema => schema.notRequired(),
  otherwise: schema => schema.required($gettext("est obligatoire"))
};

const validationSchema = yup.object({
  attributes: yup.object({
    identificationType: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo),
    firstName: yup.string().required($gettext("est obligatoire")),
    lastName: yup.string().required($gettext("est obligatoire")),
    birthdate: yup.date().required($gettext("est obligatoire")),
    birthCity: yup.string().required($gettext("est obligatoire")),
    addressStreet: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo),
    addressCity: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo),
    addressZipcode: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo),
    isCloseToPoliticalyExposed: yup.string().required($gettext("est obligatoire")),
    isPoliticalyExposed: yup.string().required($gettext("est obligatoire"))
  }),
  relationships: yup.object({
    addressCountry: yup.object({
      data: yup.object({
        id: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo)
      })
    }),
    birthCountry: yup.object({
      data: yup.object({
        id: yup.string().required($gettext("est obligatoire")),
      })
    }),
    nationalityCountry: yup.object({
      data: yup.object({
        id: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo)
      })
    }),
    industrySector: yup.object({
      data: yup.object({
        id: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo)
      })
    }),
    profession: yup.object({
      data: yup.object({
        id: yup.string().when("$attributes.addedAsUbo", skipWhenAddedAsUbo)
      })
    })
  })
});

const { errors, handleSubmit, defineField, setValues, setFieldValue } = useForm({
  validationSchema: validationSchema,
  initialValues: props.physicalPerson
});

const { preselectMultipleCountryFields } = useCountryDetection({
  countries: computed(() => countryStore.sortedCountries),
  setFieldValue
});

const [addedAsUbo, addedAsUboAttrs] = defineField("attributes.addedAsUbo");
const [firstName, firstNameAttrs] = defineField("attributes.firstName");
const [lastName, lastNameAttrs] = defineField("attributes.lastName");
const [addressStreet, addressStreetAttrs] = defineField("attributes.addressStreet");
const [addressCity, addressCityAttrs] = defineField("attributes.addressCity");
const [addressZipcode, addressZipcodeAttrs] = defineField("attributes.addressZipcode");
const [birthdate, birthdateAttrs] = defineField("attributes.birthdate");
const [birthCity, birthCityAttrs] = defineField("attributes.birthCity");
const [identificationType, identificationTypeAttrs] = defineField("attributes.identificationType");
const [addressCountryId, addressCountryIdAttrs] = defineField("relationships.addressCountry.data.id");
const [birthCountryId, birthCountryIdAttrs] = defineField("relationships.birthCountry.data.id");
const [nationalityCountryId, nationalityCountryIdAttrs] = defineField("relationships.nationalityCountry.data.id");
const [industrySectorId, industrySectorIdAttrs] = defineField("relationships.industrySector.data.id");
const [professionId, professionIdAttrs] = defineField("relationships.profession.data.id");
const [isCloseToPoliticalyExposed, isCloseToPoliticalyExposedAttrs] = defineField("attributes.isCloseToPoliticalyExposed");
const [isPoliticalyExposed, isPoliticalyExposedAttrs] = defineField("attributes.isPoliticalyExposed");
const [physicalPersonDocuments, physicalPersonDocumentsAttrs] = defineField("relationships.physicalPersonDocuments.data");

const onFormSubmit = handleSubmit((values, actions) => {
  const payload = {
    type: "physicalPerson",
    ...values
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

const handleAddressUpdate = (address) => {
  const components = address?.address_components || [];

  const streetNumber = components.find(c => c.types.includes("street_number"))?.long_name || "";
  const streetName = components.find(c => c.types.includes("route"))?.long_name || "";
  const city = components.find(c => c.types.includes("locality"))?.long_name || "";
  const postalCode = components.find(c => c.types.includes("postal_code"))?.long_name || "";
  const countryCode = components.find(c => c.types.includes("country"))?.short_name || "";

  setFieldValue("attributes.addressStreet", `${streetName} ${streetNumber}`.trim());
  setFieldValue("attributes.addressCity", city);
  setFieldValue("attributes.addressZipcode", postalCode);

  const matchedCountry = findCountryByCode(countryStore.sortedCountries, countryCode);
  if (matchedCountry) {
    setFieldValue("relationships.addressCountry.data.id", matchedCountry.id);
  } else {
    setFieldValue("relationships.addressCountry.data.id", null);
  }
};

watch(() => props.physicalPerson, (newPhysicalPerson) => {
  setValues(newPhysicalPerson);
});

const preselectCountries = () => {
  if (!countryStore.sortedCountries || countryStore.sortedCountries.length === 0) {
    return;
  }

  const fieldsToPreselect = [
    {
      fieldPath: "relationships.birthCountry.data.id",
      currentValue: birthCountryId
    },
    {
      fieldPath: "relationships.nationalityCountry.data.id",
      currentValue: nationalityCountryId
    }
  ];

  const fields = addedAsUbo.value
    ? fieldsToPreselect.filter(f => f.fieldPath.includes('birthCountry'))
    : fieldsToPreselect;

  preselectMultipleCountryFields(fields);
};

watch(() => countryStore.sortedCountries.length, (newLength) => {
  if (newLength > 0) {
    preselectCountries();
  }
}, { immediate: true });

onMounted(() => {
  setFieldValue("attributes.addedAsUbo", props.ubo && props.ubo === true || props.ubo === "true");
});

</script>
